import { ComponentProps, FC, SVGProps } from 'react';
import ClockIcon from '../../assets/icons/status-clock.svg?react';
import OfflineIcon from '../../assets/icons/status-offline.svg?react';
import RunningIcon from '../../assets/icons/status-running.svg?react';
import { MachineStatus } from '../../types/machines/machine.types';
import { cn } from '../../utils/className';
import { formatStatus } from './machineColumnConfig';

interface StatusStyle {
  icon?: FC<SVGProps<SVGSVGElement>>;
  className: string;
}

const RUNNING: StatusStyle = {
  icon: RunningIcon,
  className: 'bg-green-100 text-green-700',
};

/**
 * The design system draws running, running with warning, ready with warning
 * and offline. Ready and busy take the running style. Not ready and unknown
 * get no icon: a machine that cannot run, or whose state EDMS did not give,
 * must never look green.
 */
const STYLES: Record<MachineStatus, StatusStyle> = {
  running: RUNNING,
  ready: RUNNING,
  busy: RUNNING,
  running_with_warning: {
    icon: RunningIcon,
    className: 'bg-amber-100 text-amber-700',
  },
  ready_with_warning: {
    icon: ClockIcon,
    className: 'bg-amber-100 text-amber-700 pr-2',
  },
  not_ready: { className: 'bg-red-100 text-red-700' },
  offline: { icon: OfflineIcon, className: 'bg-neutral-100 text-neutral-500' },
  unknown: { className: 'bg-neutral-100 text-neutral-500' },
};

interface MachineStatusBadgeProps extends ComponentProps<'div'> {
  status: MachineStatus;
}

export function MachineStatusBadge({
  status,
  className,
  ...props
}: MachineStatusBadgeProps) {
  const { icon: Icon, className: styleClassName } = STYLES[status] ?? RUNNING;

  return (
    <div
      className={cn(
        `size-caption inline-flex h-6 w-fit max-w-full shrink-0 items-center
        gap-1.5 rounded-full py-1 pr-3 pl-2 whitespace-nowrap`,
        !Icon && 'px-3',
        styleClassName,
        className
      )}
      {...props}
    >
      {Icon && <Icon aria-hidden className='h-3 w-3 shrink-0' />}
      <span className='truncate'>{formatStatus(status)}</span>
    </div>
  );
}
